import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { R2_AGGREGATE_HEALTH_CHECKS, R2_HEALTH_CHECK, R2_HEALTH_PATH, type IR2HealthReport, type R2AggregateHealthCheckName } from '@common/types/api/health';
import { environment } from '@env';
import { logger } from '@services/logger/logger.service';
import { BehaviorSubject, Observable, TimeoutError, firstValueFrom, map, timeout } from 'rxjs';

export type THealthFault = 'api' | R2AggregateHealthCheckName;

const FAULT_PRECEDENCE: readonly THealthFault[] = [
    R2_HEALTH_CHECK.AI_API,
    'api',
    R2_HEALTH_CHECK.R2_API,
    R2_HEALTH_CHECK.SETTINGS_SERVICE,
];

@Injectable({
    providedIn: 'root'
})
export class HealthService
{
    public static readonly RETRY_BASE_MS = 5_000;

    public static readonly RETRY_MAX_MS = 30_000;

    public static readonly FRESH_FOR_MS = 3_000;

    public static readonly PROBE_TIMEOUT_MS = 5_000;

    private readonly faults: BehaviorSubject<readonly THealthFault[]> =
        new BehaviorSubject<readonly THealthFault[]>([]);

    private readonly probing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private checkedAt: number | null = null;

    private inFlight: Promise<readonly THealthFault[]> | null = null;
    private retryTimer: ReturnType<typeof setTimeout> | null = null;
    private retryDelayMs = HealthService.RETRY_BASE_MS;

    constructor(private http: HttpClient) { }

    public getFaults(): readonly THealthFault[]
    {
        return this.faults.getValue();
    }

    public $getFaults(): Observable<readonly THealthFault[]>
    {
        return this.faults.asObservable();
    }

    public $isProbing(): Observable<boolean>
    {
        return this.probing.asObservable();
    }

    public isDegraded(): boolean
    {
        return this.getFaults().length > 0;
    }

    public $isDegraded(): Observable<boolean>
    {
        return this.$getFaults().pipe(map((faults) => faults.length > 0));
    }

    public isSendingBlocked(): boolean
    {
        return this.getFaults().includes(R2_HEALTH_CHECK.AI_API);
    }

    public $isSendingBlocked(): Observable<boolean>
    {
        return this.$getFaults().pipe(map((faults) => faults.includes(R2_HEALTH_CHECK.AI_API)));
    }

    public $getPrimaryFault(): Observable<THealthFault | null>
    {
        return this.$getFaults().pipe(map((faults) => HealthService.pickPrimary(faults)));
    }

    public async check(): Promise<readonly THealthFault[]>
    {
        if (this.inFlight)
        {
            return this.inFlight;
        }

        if (this.checkedAt !== null && Date.now() - this.checkedAt < HealthService.FRESH_FOR_MS)
        {
            return this.getFaults();
        }

        return this.refresh();
    }

    public async settled(): Promise<readonly THealthFault[]>
    {
        return this.inFlight ?? this.getFaults();
    }

    public refresh(): Promise<readonly THealthFault[]>
    {
        if (this.inFlight)
        {
            return this.inFlight;
        }

        this.cancelRetry();
        this.probing.next(true);

        this.inFlight = this.probe()
            .then((faults) =>
            {
                this.faults.next(faults);
                this.checkedAt = Date.now();

                if (faults.length > 0)
                {
                    this.scheduleRetry();
                }
                else
                {
                    this.retryDelayMs = HealthService.RETRY_BASE_MS;
                }

                return faults;
            })
            .finally(() =>
            {
                this.inFlight = null;
                this.probing.next(false);
            });

        return this.inFlight;
    }

    private async probe(): Promise<readonly THealthFault[]>
    {
        if (environment.check_health === false)
        {
            return [];
        }

        try
        {
            const report = await firstValueFrom(
                this.http.get<IR2HealthReport>(R2_HEALTH_PATH).pipe(
                    timeout(HealthService.PROBE_TIMEOUT_MS),
                ),
            );

            return HealthService.faultsIn(report);
        }
        catch (error)
        {
            const report = HealthService.reportIn(error);

            if (report)
            {
                return HealthService.faultsIn(report);
            }

            // A client-side failure (a broken interceptor, say) says nothing about
            // the backend, and must not put the app behind the maintenance screen.
            if (!(error instanceof HttpErrorResponse) && !(error instanceof TimeoutError))
            {
                logger.error(error);

                return [];
            }

            return error instanceof HttpErrorResponse
                ? [ 'api' ]
                : [];
        }
    }

    public static pickPrimary(faults: readonly THealthFault[]): THealthFault | null
    {
        return FAULT_PRECEDENCE.find((fault) => faults.includes(fault)) ?? faults[ 0 ] ?? null;
    }

    private static reportIn(error: unknown): IR2HealthReport | null
    {
        const body = error instanceof HttpErrorResponse ? error.error : null;

        return HealthService.isReport(body) ? body : null;
    }

    private static isReport(body: unknown): body is IR2HealthReport
    {
        return typeof body === 'object'
            && body !== null
            && typeof (body as IR2HealthReport).details === 'object'
            && (body as IR2HealthReport).details !== null;
    }

    private static faultsIn(report: IR2HealthReport): readonly THealthFault[]
    {
        return R2_AGGREGATE_HEALTH_CHECKS.filter((name) => report.details?.[ name ]?.status === 'down');
    }

    private scheduleRetry(): void
    {
        this.cancelRetry();

        const delay = this.retryDelayMs;
        this.retryDelayMs = Math.min(delay * 2, HealthService.RETRY_MAX_MS);

        this.retryTimer = setTimeout(() => void this.refresh(), delay);
    }

    private cancelRetry(): void
    {
        if (this.retryTimer !== null)
        {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }
}
